import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";

const FOOTER_LINKS = [
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
  { label: "Support Centre", to: "/support" },
  { label: "Booking Protection", to: "/booking-protection" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Data Protection", to: "/data-protection" },
];

// Centralized company details — imported by Contact page / support ticket forms.
export const COMPANY = {
  legalName: "Swaan Hotels & Resorts Private Limited",
  brand: "SwaanStay",
  email: "Support@swaanstay.com",
  phone: "9030460666",
  phoneDisplay: "+91 90304 60666",
  whatsappHref: "https://wa.me/919030460666",
};

export default function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-[#0F1115] text-slate-200 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4 space-y-3">
          <div className="inline-flex items-center rounded-2xl bg-white px-4 py-3 shadow-[0_10px_30px_-8px_rgba(200,163,77,0.55)]">
            <img src="/logo.png" alt="SwaanStay — India's First AI-Powered Hotel Marketplace · Connecting Travellers Directly with Hotels" className="h-20 w-auto" loading="lazy" />
          </div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xs font-body">
            India&apos;s first AI-powered luxury hotel marketplace. Post your Group Booking Bid, let hotels bid for you, and save on every stay.
          </p>
          <div className="pt-2 text-xs text-slate-400 space-y-1.5 font-body">
            <div className="flex items-center gap-2" data-testid="footer-company-name">
              <span className="ss-label text-[#E8D8A8]">Company</span>
              <span className="text-slate-300">{COMPANY.legalName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-[#C8A34D]" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white transition-colors" data-testid="footer-email">{COMPANY.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-[#C8A34D]" />
              <a href={`tel:+91${COMPANY.phone}`} className="hover:text-white transition-colors" data-testid="footer-phone">{COMPANY.phoneDisplay}</a>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
              <a href={COMPANY.whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-whatsapp">
                WhatsApp us instantly
              </a>
            </div>
            <div className="flex items-start gap-2 pt-1">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#C8A34D]" /> Hyderabad, Telangana, India
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="ss-label text-[#E8D8A8] mb-3">Company</div>
          <ul className="space-y-2 text-sm font-body">
            {FOOTER_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-slate-300 hover:text-[#E8D8A8] transition-colors"
                  data-testid={`footer-link-${l.to.replace('/', '')}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="ss-label text-[#E8D8A8] mb-3">Travellers</div>
          <ul className="space-y-2 text-sm font-body">
            <li><Link to="/search" className="text-slate-300 hover:text-[#E8D8A8]">Find hotels</Link></li>
            <li><Link to="/requests" className="text-slate-300 hover:text-[#E8D8A8]">Group Booking Bid</Link></li>
            <li><Link to="/rewards" className="text-slate-300 hover:text-[#E8D8A8]">Rewards</Link></li>
            <li><Link to="/wallet" className="text-slate-300 hover:text-[#E8D8A8]">My Wallet</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="ss-label text-[#E8D8A8] mb-3">Hotel partners</div>
          <ul className="space-y-2 text-sm font-body">
            <li><Link to="/hotels-portal" className="text-slate-300 hover:text-[#E8D8A8]">List your hotel</Link></li>
            <li><Link to="/partner" className="text-slate-300 hover:text-[#E8D8A8]">Partner dashboard</Link></li>
            <li><Link to="/partner/ai-drafts" className="text-slate-300 hover:text-[#E8D8A8]">AI Auto-Bidding</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="ss-label text-slate-500">© 2026 {COMPANY.legalName} — All rights reserved.</div>
          <div className="ss-label text-slate-500">CIN pending · GSTIN pending · Made with <span className="text-[#C8A34D]">♦</span> in India</div>
        </div>
      </div>
    </footer>
  );
}
